import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Table, TableColumn, Tooltip, Typography } from '@scale/scaleui';
import { ITaskAttemptToReturnJson } from '@scale/shared/types/taskattempt';
import { useMemo } from 'react';
import { ToolsLink } from 'react-scale/components/ExpertManagement/ExpertManagementPage/ToolsLink';
import { useTableFilteringContext } from 'react-scale/components/ExpertManagement/hooks/useTableFiltering';
import ProductPill from 'react-scale/corp/components/UI/ProductPill';
import FilterComponent from 'react-scale/dashboard.scale.com/components/SelfServe/ModelEval/ModelEvalRevamp/SharedComponents/FilterComponent';
import { LoadingTable } from './LoadingTable';
import { OUTLIER_FRONTEND_URL } from 'react-scale/lib/constants';
import { buildURL } from 'react-scale/lib/urls';

type TaskAttemptWithProject = ITaskAttemptToReturnJson & {
  project: { _id: string; name: string };
};

interface RecentTaskAttemptsTableProps {
  title?: string;
  data: TaskAttemptWithProject[] | null;
  isLoading: boolean;
}

export const LastTaskAttemptsTable: React.FC<RecentTaskAttemptsTableProps> = ({
  title = 'Last Task Attempts',
  data,
  isLoading,
}: RecentTaskAttemptsTableProps) => {
  const RecentTaskAttemptsTableColumns: TableColumn[] = [
    {
      id: 'project.name',
      header: 'Project',
      cell: function ProjectCell(props: any) {
        const projectId: string = props.row.original.project._id;
        const projectName: string = props.row.original.project.name;
        return (
          <div className="flex flex-col">
            <ToolsLink to={`/lookup/${projectId}`} className="flex items-center">
              <Typography.Paragraph2>{projectName}</Typography.Paragraph2>
              <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-2" />
            </ToolsLink>
            <div className="text-xs">{projectId}</div>
          </div>
        );
      },
    },
    {
      id: 'id',
      header: 'Task Attempt',
      cell: function ProjectCell(props: any) {
        const { id } = props.row.original;
        return (
          <ToolsLink to={`/lookup/${id}`} className="flex items-center">
            <Typography.Paragraph2>{id}</Typography.Paragraph2>
            <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-2" />
          </ToolsLink>
        );
      },
    },
    {
      id: 'taskType',
      header: 'Task Type',
      cell: function TaskTypeCell(props: any) {
        const { taskType } = props.row.original;
        return <ProductPill type={taskType} />;
      },
    },
    {
      id: 'attemptedAtReviewLevel',
      header: 'Attempted at Review Level',
    },
    {
      id: 'taskReviewLevel',
      header: 'Task Review Level',
      cell: function TaskReviewLevelCell(props: any) {
        const { taskReviewLevel, isCompleted } = props.row.original;
        return isCompleted ? 'Completed' : taskReviewLevel;
      },
    },
    {
      id: 'isLatestAttempt',
      header: 'Latest Attempt on Task',
      cell: function IsLatestAttemptOnTaskCell(props: any) {
        const { isLatestAttempt } = props.row.original;
        return isLatestAttempt ? 'Yes' : 'No';
      },
    },
    {
      id: 'isClaimed',
      header: 'Is Claimed',
      cell: function ForceClaimCell(props: any) {
        const { isClaimed } = props.row.original;
        return isClaimed ? 'Yes' : 'No';
      },
    },
    {
      id: 'forceClaimLink',
      header: 'Force Claim',
      cell: function ForceClaimCell(props: any) {
        const { taskId, isClaimed, isLatestAttempt, taskReviewLevel, isCompleted } =
          props.row.original;
        return (
          <Button
            size="sm"
            variant="neutral"
            disabled={
              isClaimed || !isLatestAttempt || [11, 12].includes(taskReviewLevel) || isCompleted
            }
            onClick={() => {
              window.open(
                buildURL(OUTLIER_FRONTEND_URL, '/tasks', { [`taskId`]: taskId, forceClaim: 1 }),
                '_blank',
              );
            }}
          >
            Claim
          </Button>
        );
      },
    },
    {
      id: 'taskFeedback',
      header: 'Review Feedback',
      cell: function ResponseCell(props: any) {
        const { taskFeedback } = props.row.original;
        const feedback = taskFeedback?.[0]?.response?.toString() ?? '';
        return (
          <Tooltip
            targetClassName="inline"
            text={
              feedback ? (
                <div>
                  {feedback
                    .match(/.{1,150}/g) // this splits the string into chunks of 150 characters, can be improved to split by word instead
                    .map((s: string, i: number) => (
                      <div key={i}>{s}</div>
                    ))}
                </div>
              ) : (
                ''
              )
            }
            target={<div>{feedback.slice(0, 20) + (feedback.length > 20 ? '..' : '')}</div>}
          />
        );
      },
    },
    {
      id: 'attemptedAt',
      header: 'Attempted At',
      cell: function AttemptedAtCell(props: any) {
        const { attemptedAt } = props.row.original;
        return new Date(attemptedAt).toLocaleString();
      },
    },
    {
      id: 'timeSpentSecs',
      header: 'Time Spent',
      cell: function TimeSpentCell(props: any) {
        const { timeSpentSecs } = props.row.original;
        const minutes = Math.round(timeSpentSecs / 60);
        return `${minutes} mins`;
      },
    },
  ];

  const enrichedData = useMemo(() => {
    return (data ?? []).map(d => {
      return {
        ...d,
        projectKey: d.project._id + '_' + d.project.name,
      };
    });
  }, [data]);

  const { filters, filteredData, fieldMap, setFilters, exactFieldOptionsMap, fieldOptions } =
    useTableFilteringContext({
      data: enrichedData,
      fieldFilters: {
        projectKey: { name: 'Project', type: 'fuzzy' },
        attemptedAtReviewLevel: { name: 'Review Level', type: 'number' },
      },
    });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Typography.Subtitle2>{title}</Typography.Subtitle2>{' '}
      </div>
      <div>
        <FilterComponent
          filters={filters}
          setFilters={setFilters}
          fieldMap={fieldMap}
          exactFieldOptionsMap={exactFieldOptionsMap}
          fieldOptions={fieldOptions}
        />
      </div>
      {isLoading ? (
        <LoadingTable columns={RecentTaskAttemptsTableColumns} numRows={1} rowHeight={1} />
      ) : (
        <Table columns={RecentTaskAttemptsTableColumns} data={filteredData} />
      )}
    </div>
  );
};
